import { skyEncounterBeat } from './88-sky-encounter-beat.mjs';
import { SKY_WORLD, SKY_WORLD_ASSETS, skyBossWorldScale } from './86-sky-world-scale.mjs';
import {
  sampleSkyPrototypeRig,
  skyPrototypeMouth,
  skyDragonBreathing,
  skyPrototypeNostrils,
  skyPrototypeHeadFocus,
} from './84-sky-prototype-rig.mjs';
import { skyBossDesign } from './85-sky-boss-designs.mjs';

/** Authored boss sockets in the GLB's metre-scale, Y-up/+Z-forward bind space.
 * These transforms are shared with the actual hit-bearing model joints. Art may
 * change decorative geometry freely; moving a socket requires an anchor test. */
const socket = (id, node, parent, pivot, offset, localScale = 1, clone = 0) => ({
  id,
  node,
  parent,
  pivot,
  offset,
  localScale,
  clone,
});
const PROFILES = {
  serpent: {
    model: 'leviathan',
    scale: 2,
    radius: 0.84,
    critical: 0.34,
    body: 3.6,
    label: 'THROAT',
    sockets: [socket('throat', 'weakpoint_core', 'head', [0, 0, 0], [0, -0.18, 1.6])],
  },
  twin: {
    model: 'twin',
    scale: 2,
    radius: 0.72,
    critical: 0.3,
    body: 5,
    label: 'CROWN THROAT',
    sockets: [-1, 1].map((side, index) =>
      socket(
        `throat-${index}`,
        'weakpoint_core',
        index ? 'head_right' : 'head',
        [side * 1.3, 0, 0],
        [0, -0.18, 1.6],
        0.8,
      ),
    ),
  },
  skeletalSerpent: {
    model: 'skeletal',
    scale: 2,
    radius: 0.8,
    critical: 0.32,
    body: 3.8,
    label: 'RIB HEART',
    sockets: [socket('heart', 'weakpoint_core', null, [0, 0, 0], [0, 0.15, -2.3])],
  },
  skeletalTwin: {
    model: 'skeletal',
    scale: 2,
    radius: 0.72,
    critical: 0.3,
    body: 6.2,
    label: 'RIB HEART',
    sockets: [-1, 1].map((side, index) =>
      socket(
        `heart-${index}`,
        'weakpoint_core',
        null,
        [side * 2.1, 0, 0],
        [0, 0.15, -2.3],
        0.8,
        index,
      ),
    ),
  },
  stoneOracle: {
    model: 'mask',
    scale: 1.5,
    radius: 0.78,
    critical: 0.3,
    body: 3.5,
    label: 'STERNUM FISSURE',
    sockets: [socket('sternum', 'weakpoint_core', 'torso', [0, 0, 0], [0, 0.2, 1])],
  },
  mech: {
    model: 'titan',
    scale: 1.6,
    radius: 0.78,
    critical: 0.3,
    body: 3.5,
    label: 'REACTOR VENT',
    sockets: [socket('reactor', 'weakpoint_core', 'torso', [0, 0, 0], [0, 0.25, 1])],
  },
  solarTitan: {
    model: 'titan',
    scale: 1.6,
    radius: 0.78,
    critical: 0.3,
    body: 3.5,
    label: 'SOLAR REACTOR',
    sockets: [socket('reactor', 'weakpoint_core', 'torso', [0, 0, 0], [0, 0.25, 1])],
  },
  oracle: {
    model: 'sentinel',
    scale: 1.5,
    radius: 0.72,
    critical: 0.28,
    body: 3.2,
    label: 'LENS APERTURE',
    sockets: [socket('lens', 'weakpoint_core', 'head', [0, 0, 0], [0, 0.1, 1.1])],
  },
  swarm: {
    model: 'nautilus',
    scale: 1.5,
    radius: 0.82,
    critical: 0.32,
    body: 3.3,
    label: 'LEADER MANTLE',
    sockets: [socket('mantle', 'weakpoint_core', null, [0, 0, 0], [0, 0, 1.05])],
  },
  shellSwarm: {
    model: 'nautilus',
    scale: 1.5,
    radius: 0.82,
    critical: 0.32,
    body: 3.3,
    label: 'SHELL MANTLE',
    sockets: [socket('mantle', 'weakpoint_core', null, [0, 0, 0], [0, 0, 1.05])],
  },
  carrier: {
    model: 'carrier',
    scale: 1.5,
    radius: 0.82,
    critical: 0.3,
    body: 6.2,
    label: 'TURBINE INTAKE',
    sockets: [-1, 1].map((side, index) =>
      socket(
        `intake-${index}`,
        index ? 'weakpoint_right' : 'weakpoint_left',
        null,
        [0, 0, 0],
        [side * 3.1, -0.1, 1],
      ),
    ),
  },
};
for (const descriptor of Object.values(PROFILES)) {
  for (const anchor of descriptor.sockets) {
    Object.freeze(anchor.pivot);
    Object.freeze(anchor.offset);
    Object.freeze(anchor);
  }
  Object.freeze(descriptor.sockets);
  Object.freeze(descriptor);
}
Object.freeze(PROFILES);
const finite = (value, fallback = 0) => (Number.isFinite(value) ? value : fallback);
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
/** Rotate a point using Three/glTF's XYZ Euler convention, without importing a renderer. */
export function skyRotateXYZ(point, rotation) {
  let [x, y, z] = point;
  const [a, b, c] = rotation;
  [x, y] = [x * Math.cos(c) - y * Math.sin(c), x * Math.sin(c) + y * Math.cos(c)];
  [x, z] = [x * Math.cos(b) + z * Math.sin(b), -x * Math.sin(b) + z * Math.cos(b)];
  return [x, y * Math.cos(a) - z * Math.sin(a), y * Math.sin(a) + z * Math.cos(a)];
}
/** Canonical root bank in radians. Reduced-motion presentation must retain this
 * small hit-bearing rotation; only non-gameplay flutter may be suppressed. */
export function skyGuardianRootRotation(item) {
  const charge = clamp(finite(item.charge), 0, 1),
    recoil = clamp(finite(item.recoil), 0, 1);
  return [
    clamp(finite(item.vy) * -0.03, -0.12, 0.12) - recoil * 0.045,
    clamp(finite(item.vx) * 0.035, -0.22, 0.22) * (1 - charge * 0.7),
    clamp(finite(item.vx) * -0.055, -0.24, 0.24) * (1 - charge * 0.75),
  ];
}
/** Return one/two physical sockets, exact world positions and finite exposure.
 * Shared88 charge and facing/cooling windows own physical cover opening. Alternating sockets are selected
 * once per attack cycle, never chase the player, and stay inside the flight lane.
 * Unknown profiles use the original serpent anatomy; absent age is entry/closed. */
export function skyGuardianAnatomy(item) {
  const original = PROFILES[item.profile] ?? PROFILES.serpent;
  const physical = skyBossWorldScale(item.profile, item.design);
  const descriptor = {
    ...original,
    model: physical.model,
    scale: physical.modelScale,
    radius: original.radius * physical.contactScale,
    critical: original.critical * physical.contactScale,
    body: original.body * physical.contactScale,
  };
  const phase = clamp(Math.trunc(item.combatPhase) || 1, 1, 3);
  const beat = skyEncounterBeat(phase, item.phaseAge);
  const selected = (beat.cycle + phase - 1) % descriptor.sockets.length;
  const inCombat =
    finite(item.age) >= 4.5 &&
    !item.defeated &&
    !item.traversing &&
    !item.breath?.active &&
    !item.aimPreparing;
  const opening = inCombat ? beat.opening : 0;
  const rootRotation = skyGuardianRootRotation(item);
  const time = finite(item.age) + finite(item.animationOffset);
  const design = skyBossDesign(item.design);
  const rate = design?.travel[0] ?? 1;
  const charge = clamp(finite(item.charge), 0, 1),
    recoil = clamp(finite(item.recoil), 0, 1);
  const reach = design && item.design !== 0 ? 1.7 : 1;
  // Heads lead the torso with an authored phase offset. These exact transforms
  // drive both visible hinges and contacts, including anticipation and recoil.
  const head = (offset) => [
    Math.sin(time * 1.7 * rate + offset) * 0.035 - charge * 0.035 + recoil * 0.055,
    Math.sin(time * 1.05 * rate + offset) * 0.09 * reach * (1 - charge * 0.45),
    0,
  ];
  const joints = {
    head: head(0),
    head_right: head(0.7),
    torso: [
      Math.sin(time * 1.4 * rate - 0.2) * 0.025 + charge * 0.025 - recoil * 0.035,
      Math.sin(time * 1.025 * rate - 0.3) * 0.055 * reach,
      0,
    ],
  };
  if (item.artVariant === 'first-dragon') {
    const target = item.gazeTarget;
    const relative =
      Array.isArray(target) && target.length === 3 && target.every(Number.isFinite)
        ? target.map((v, i) => v - [finite(item.x), finite(item.y), finite(item.z)][i])
        : null;
    const localTarget = relative
      ? [
          [1, 0, 0],
          [0, 1, 0],
          [0, 0, 1],
        ].map((axis) =>
          skyRotateXYZ(axis, rootRotation).reduce((sum, v, i) => sum + v * relative[i], 0),
        )
      : undefined;
    const breathing = skyDragonBreathing(item);
    const sampled = sampleSkyPrototypeRig(
      item.prototypeRig,
      time,
      opening,
      localTarget,
      Math.max(opening, charge),
      breathing.inflation,
    );
    if (sampled) {
      const world = (local) =>
        skyRotateXYZ(local, rootRotation).map(
          (v, axis) => v + [finite(item.x), finite(item.y, 4), finite(item.z, -40)][axis],
        );
      const [x, y, z] = world(sampled.position);
      const reachable =
        x >= -11.8 && x <= 11.8 && y >= SKY_WORLD.pilotAimHeight && y <= SKY_WORLD.maximumAimHeight;
      const target = {
        id: 'prototype-crown',
        node: 'weakpoint_core',
        parent: null,
        clone: 0,
        x,
        y,
        z,
        radius:
          sampled.rig.socket.radius * SKY_WORLD_ASSETS.dragon.modelScale * sampled.chestExpansion,
        criticalRadius:
          sampled.rig.socket.criticalRadius *
          SKY_WORLD_ASSETS.dragon.modelScale *
          sampled.chestExpansion,
        active: true,
        exposed: inCombat && reachable && beat.vulnerable,
        telegraph: inCombat && beat.telegraph,
        opening,
        label: 'CRANIAL CREST',
      };
      const [bx, by, bz] = world(
        sampled.rig.body.center.map(
          (v) => v * sampled.rig.modelScale * SKY_WORLD_ASSETS.dragon.modelScale,
        ),
      );
      return {
        model: 'prototypeDragon',
        breathing,
        headFocus: (() => {
          const [hx, hy, hz] = world(skyPrototypeHeadFocus(sampled) ?? sampled.position);
          return { x: hx, y: hy, z: hz };
        })(),
        nostrils: skyPrototypeNostrils(sampled).map((anchor) => {
          const [nx, ny, nz] = world(anchor.position);
          const [dx, dy, dz] = skyRotateXYZ(anchor.direction, rootRotation);
          return { x: nx, y: ny, z: nz, dx, dy, dz };
        }),
        attackSocket: (() => {
          const local = skyPrototypeMouth(sampled);
          if (!local) return target;
          const [mx, my, mz] = world(local);
          return { x: mx, y: my, z: mz };
        })(),
        scale: sampled.rig.modelScale * SKY_WORLD_ASSETS.dragon.modelScale,
        rootRotation,
        joints: {},
        rigPose: sampled.joints,
        targets: [target],
        primary: target,
        body: {
          x: bx,
          y: by,
          z: bz,
          radius:
            sampled.rig.body.radius * SKY_WORLD_ASSETS.dragon.modelScale * sampled.chestExpansion,
        },
      };
    }
  }
  const targets = descriptor.sockets.map((anchor, index) => {
    const rotation = anchor.parent ? joints[anchor.parent] : [0, 0, 0];
    let position = skyRotateXYZ(
      anchor.offset.map((v) => v * anchor.localScale),
      rotation,
    );
    position = position.map((v, axis) => (v + anchor.pivot[axis]) * descriptor.scale);
    position = skyRotateXYZ(position, rootRotation);
    const [x, y, z] = position.map(
      (v, axis) => v + [finite(item.x), finite(item.y, 4), finite(item.z, -40)][axis],
    );
    const active = index === selected;
    const reachable =
      x >= -11.8 && x <= 11.8 && y >= SKY_WORLD.pilotAimHeight && y <= SKY_WORLD.maximumAimHeight;
    return {
      ...anchor,
      x,
      y,
      z,
      radius: descriptor.radius,
      criticalRadius: descriptor.critical,
      active,
      exposed: inCombat && active && reachable && beat.vulnerable,
      telegraph: inCombat && active && beat.telegraph,
      opening: active ? opening : 0,
      label: descriptor.label,
    };
  });
  return {
    model: descriptor.model,
    scale: descriptor.scale,
    rootRotation,
    joints,
    targets,
    primary: targets[selected],
    body: {
      x: finite(item.x),
      y: finite(item.y, 4),
      z: finite(item.z, -40) + 2.8 * physical.contactScale,
      radius: descriptor.body,
    },
  };
}
